import React, { Reducer, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core'
import { HeaderBlock } from 'components/header-block/HeaderBlock'
import PageContainer from 'components/page-container/PageContainer'
import { getContentType } from 'state/constants/ContentType'
import SimpleTable from 'components/table/Table'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FixMeLater } from 'types/FixMeLaterType'
import { Action } from 'redux'
import { RootState } from 'state/reducers/AppReducer'
import { getDashboardLayouts, setDashboardLayout } from 'state/actions/DashboardLayoutActions'
import { Loading } from 'components/loading/Loading'

interface Props {
    history: FixMeLater
}

interface TableHeader {
    id: String,
    clickable?: boolean | null,
    align?: string | null,
    label: string,
    action?: {
        identifier: string,
        path: string
    } | null
}

interface TableCell {
    id: String,
    handleClick?: ((event: Event, row: FixMeLater) => void) | null,
    align?: string | null,
    clickable?: boolean | null
}

function ListDashboardLayoutsPage(props: Props) {

    const dashboardLayouts = useSelector<RootState, FixMeLater>(state => state.DashboardLayoutReducer.dashboardLayouts)

    const [t, i18n] = useTranslation()
    const dispatch = useDispatch()

    const [loading, setLoading] = useState(false)
    const [newDashboardLayoutModalOpen, setNewDashboardLayoutModalOpen] = useState(false)
    const [newLayoutIdValue, setNewLayoutIdValue] = useState("")
    const [newLayoutTypeValue, setNewLayoutTypeValue] = useState("")

    useEffect(() => {
        setLoading(true)
        dispatch(getDashboardLayouts(() => {
            setLoading(false)
        }))
    }, [])

    function createHeaders(): TableHeader[] {
        return [
            {
                id: "id",
                clickable: true,
                align: "left",
                action: { identifier: "id", path: "dashboard-layouts/{id}" },
                label: t('dashboard-layout.dashboard-name')
            },
            {
                id: "Type",
                clickable: false,
                align: "left",
                label: t('dashboard-layout.dashboard-type')
            }
        ];
    }

    function createCells(headers: TableHeader[]) {
        let cells: TableCell[] = [];

        headers.forEach(header => {
            let cell: TableCell = { id: header.id };

            if (header.hasOwnProperty("action")) {
                cell.handleClick = (_: Event, row: FixMeLater) => {
                    let path = header.action?.path.replace(
                        "{id}",
                        row[header.action?.identifier]
                    );
                    props.history.push(path);
                };
            }

            cell.align = header.align;
            cell.clickable = header.clickable;

            cells.push(cell);
        });

        return cells;
    }

    function handleAddDashboardLayout() {
        const dashboardLayout = {
            id: newLayoutIdValue,
            Type: newLayoutTypeValue,
            LayoutCells: []
        }

        dispatch(setDashboardLayout(dashboardLayout, (success: boolean) => {
            if (success) {
                props.history.push(`dashboard-layouts/${dashboardLayout.id}`);
            }
        }))
    }

    function renderContent() {
        const headers = createHeaders();
        const cells = createCells(headers);

        return (
            <SimpleTable
                isEditing
                isOrdered
                // isSticky
                isSelectable={false}
                dataId={"id"}
                headers={headers}
                cells={cells}
                rows={Object.values(dashboardLayouts)}
            />
        )
    }

    function renderNewDashboardLayoutModal() {
        return (
            <Dialog
                open={newDashboardLayoutModalOpen}
            >
                <DialogTitle>
                    {t('dashboard-layout.new-dashboard-layout')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={newLayoutIdValue}
                        label={t('dashboard-layout.layout-id')}
                        onChange={(e) => setNewLayoutIdValue(e.target.value)}
                        helperText={t('dashboard-layout.unique-id-helper')}
                    />
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={t('dashboard-layout.layout-type')}
                        value={newLayoutTypeValue}
                        onChange={(e) => setNewLayoutTypeValue(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                            setNewDashboardLayoutModalOpen(false)
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                            if (newLayoutIdValue !== "") {
                                setNewDashboardLayoutModalOpen(false)
                                handleAddDashboardLayout()
                            }
                        }}
                    >
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <Box>
            <HeaderBlock
                title={t('dashboard-layout.dashboard-layouts')}
                right={
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setNewDashboardLayoutModalOpen(true)}
                    >
                        {t('dashboard-layout.add-dashboard')}
                    </Button>
                }
            />

            {renderNewDashboardLayoutModal()}

            <PageContainer>
                {loading ? <Loading /> : renderContent()}
            </PageContainer>
        </Box>
    )
}

ListDashboardLayoutsPage.propTypes = {

}

export default ListDashboardLayoutsPage

